export interface WalletAmountRule {
  min: bigint;
  step: bigint;
}

/**
 * Web-only wallet rules for the first client port. Keeping deposit and payback separate makes a
 * future server-driven implementation a data-source change instead of another UI rewrite.
 */
export const DEPOSIT_AMOUNT_RULE: Readonly<WalletAmountRule> = {
  min: 100_000n,
  step: 100_000n,
};

export const PAYBACK_AMOUNT_RULE: Readonly<WalletAmountRule> = {
  min: 100_000n,
  step: 100_000n,
};

export type WalletAmountValidation = 'required' | 'below-minimum' | 'off-step' | 'valid';

function requireValidRule(rule: Readonly<WalletAmountRule>): void {
  if (rule.min <= 0n || rule.step <= 0n) throw new RangeError('Wallet amount rules must be positive');
}

/** The first step-aligned amount that also satisfies the configured minimum. */
export function minimumWalletAmount(rule: Readonly<WalletAmountRule>): bigint {
  requireValidRule(rule);
  return ((rule.min + rule.step - 1n) / rule.step) * rule.step;
}

export function validateWalletAmount(amount: bigint, rule: Readonly<WalletAmountRule>): WalletAmountValidation {
  requireValidRule(rule);
  if (amount <= 0n) return 'required';
  if (amount < rule.min) return 'below-minimum';
  if (amount % rule.step !== 0n) return 'off-step';
  return 'valid';
}

/** Select the next valid amount. Blank/zero values start at the first allowed amount. */
export function nextWalletAmount(amount: bigint, rule: Readonly<WalletAmountRule>): bigint {
  const minimum = minimumWalletAmount(rule);
  if (amount < minimum) return minimum;
  const roundedUp = ((amount + rule.step - 1n) / rule.step) * rule.step;
  return roundedUp === amount ? roundedUp + rule.step : roundedUp;
}

/** Select the previous valid amount without ever dropping below the first allowed amount. */
export function previousWalletAmount(amount: bigint, rule: Readonly<WalletAmountRule>): bigint {
  const minimum = minimumWalletAmount(rule);
  if (amount <= minimum) return minimum;
  const roundedDown = (amount / rule.step) * rule.step;
  const previous = roundedDown === amount ? roundedDown - rule.step : roundedDown;
  return previous < minimum ? minimum : previous;
}

/** Largest valid payback amount that cannot exceed the available balance, or zero when unavailable. */
export function maximumWalletAmount(balance: bigint, rule: Readonly<WalletAmountRule>): bigint {
  const minimum = minimumWalletAmount(rule);
  if (balance < minimum) return 0n;
  const roundedDown = (balance / rule.step) * rule.step;
  return roundedDown >= minimum ? roundedDown : 0n;
}
